import { useMemo } from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend } from 'recharts';
import { Heart, TrendingUp, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCKPIs } from '@/hooks/useGSCData';
import { useGA4KPIs } from '@/hooks/useGA4Data';
import { useAuthStore } from '@/stores/auth-store';

interface HealthDimension {
  dimension: string;
  score: number;
  benchmark: number;
  label: string;
  description: string;
  status: 'good' | 'warning' | 'poor';
  tip: string;
}

/**
 * Health Radar View - Spider/Radar chart showing site health across dimensions
 * A holistic view of site performance across multiple metrics
 */
export function HealthRadarView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;
  const hasGA4 = !!activeProperty?.ga4Property;

  const { data: gscKpis, isLoading: gscLoading } = useGSCKPIs();
  const { data: ga4Kpis, isLoading: ga4Loading } = useGA4KPIs();

  const isLoading = gscLoading || ga4Loading;

  // Calculate health dimensions
  const dimensions = useMemo((): HealthDimension[] => {
    const dims: HealthDimension[] = [];

    if (gscKpis) {
      // Visibility (based on impressions trend)
      const visibilityScore = Math.min(100, Math.max(0, 50 + gscKpis.impressionsChange));
      dims.push({
        dimension: 'Visibility',
        score: visibilityScore,
        benchmark: 50,
        label: 'Visibility',
        description: 'How often your site appears in search',
        status: visibilityScore > 60 ? 'good' : visibilityScore > 40 ? 'warning' : 'poor',
        tip: visibilityScore < 50 ? 'Focus on creating more content targeting search queries' : 'Your visibility is healthy!',
      });

      // CTR Health
      const ctrScore = Math.min(100, gscKpis.ctr * 10);
      dims.push({
        dimension: 'CTR',
        score: ctrScore,
        benchmark: 30,
        label: 'Click-Through Rate',
        description: 'How often people click your results',
        status: ctrScore > 50 ? 'good' : ctrScore > 20 ? 'warning' : 'poor',
        tip: ctrScore < 30 ? 'Improve meta titles and descriptions to attract more clicks' : 'Great CTR performance!',
      });

      // Position Health (inverted - lower is better)
      const positionScore = Math.min(100, Math.max(0, 100 - (gscKpis.position * 5)));
      dims.push({
        dimension: 'Ranking',
        score: positionScore,
        benchmark: 50,
        label: 'Average Ranking',
        description: 'Your average position in search results',
        status: positionScore > 70 ? 'good' : positionScore > 40 ? 'warning' : 'poor',
        tip: positionScore < 50 ? 'Focus on content quality and backlinks to improve rankings' : 'Strong ranking performance!',
      });
    }

    if (ga4Kpis) {
      // Traffic Growth
      const trafficScore = Math.min(100, Math.max(0, 50 + ga4Kpis.sessionsChange));
      dims.push({
        dimension: 'Traffic',
        score: trafficScore,
        benchmark: 50,
        label: 'Traffic Growth',
        description: 'How your session count is changing',
        status: trafficScore > 60 ? 'good' : trafficScore > 40 ? 'warning' : 'poor',
        tip: trafficScore < 50 ? 'Diversify traffic sources and increase content output' : 'Healthy traffic growth!',
      });

      // Engagement (based on bounce rate - inverted)
      const engagementScore = Math.min(100, Math.max(0, 100 - ga4Kpis.bounceRate));
      dims.push({
        dimension: 'Engagement',
        score: engagementScore,
        benchmark: 40,
        label: 'User Engagement',
        description: 'How engaged visitors are with your content',
        status: engagementScore > 60 ? 'good' : engagementScore > 40 ? 'warning' : 'poor',
        tip: engagementScore < 50 ? 'Improve content relevance and page load speed' : 'Users are engaged with your content!',
      });

      // User Growth
      const userGrowthScore = Math.min(100, Math.max(0, 50 + ga4Kpis.usersChange));
      dims.push({
        dimension: 'Users',
        score: userGrowthScore,
        benchmark: 50,
        label: 'User Growth',
        description: 'How your unique visitor count is changing',
        status: userGrowthScore > 60 ? 'good' : userGrowthScore > 40 ? 'warning' : 'poor',
        tip: userGrowthScore < 50 ? 'Focus on acquisition channels and brand awareness' : 'Strong user growth!',
      });
    }

    return dims;
  }, [gscKpis, ga4Kpis]);

  // Overall health score
  const overallScore = useMemo(() => {
    if (dimensions.length === 0) return null;
    return dimensions.reduce((sum, d) => sum + d.score, 0) / dimensions.length;
  }, [dimensions]);

  const getHealthLabel = (score: number | null) => {
    if (score === null) return { label: 'Unknown', color: 'muted' };
    if (score >= 70) return { label: 'Excellent', color: 'success' };
    if (score >= 50) return { label: 'Good', color: 'success' };
    if (score >= 35) return { label: 'Fair', color: 'warning' };
    return { label: 'Needs Attention', color: 'danger' };
  };

  const health = getHealthLabel(overallScore);

  if (!hasGSC && !hasGA4) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Heart className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No property connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Heart className="h-6 w-6 text-danger" />
          Site Health Radar
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          A holistic view of your site's performance across key dimensions
        </p>
      </div>

      {/* Overall Health Score */}
      <div className="glass-panel p-6 text-center">
        <div className="flex items-center justify-center gap-8">
          <div>
            <p className="text-sm text-muted-foreground mb-2">Overall Health Score</p>
            {isLoading ? (
              <div className="h-16 w-16 rounded-full bg-muted animate-pulse mx-auto" />
            ) : (
              <div className={cn(
                'h-20 w-20 rounded-full flex items-center justify-center text-3xl font-bold ring-4 mx-auto',
                health.color === 'success' && 'bg-success/20 text-success ring-success/30',
                health.color === 'warning' && 'bg-warning/20 text-warning ring-warning/30',
                health.color === 'danger' && 'bg-danger/20 text-danger ring-danger/30',
                health.color === 'muted' && 'bg-muted text-muted-foreground ring-muted'
              )}>
                {overallScore?.toFixed(0) ?? '-'}
              </div>
            )}
            <p className={cn(
              'text-lg font-medium mt-2',
              health.color === 'success' && 'text-success',
              health.color === 'warning' && 'text-warning',
              health.color === 'danger' && 'text-danger'
            )}>
              {health.label}
            </p>
          </div>

          {/* Radar Chart */}
          <div className="flex-1 h-64">
            {dimensions.length > 0 && !isLoading && (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={dimensions} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                  <PolarGrid stroke="#374151" />
                  <PolarAngleAxis
                    dataKey="dimension"
                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tick={{ fill: '#6b7280', fontSize: 10 }}
                    tickCount={5}
                  />
                  <Radar
                    name="Your Score"
                    dataKey="score"
                    stroke="#8b5cf6"
                    fill="#8b5cf6"
                    fillOpacity={0.4}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Benchmark"
                    dataKey="benchmark"
                    stroke="#6b7280"
                    fill="none"
                    strokeDasharray="5 5"
                    strokeWidth={1}
                  />
                  <Legend
                    wrapperStyle={{ color: '#9ca3af', fontSize: 12 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Dimension Details */}
      <div className="grid grid-cols-2 gap-4">
        {dimensions.map((dim) => (
          <DimensionCard key={dim.dimension} dimension={dim} isLoading={isLoading} />
        ))}
      </div>

      {/* Legend */}
      <div className="glass-panel p-4">
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-primary mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">About the Health Radar</p>
            <p className="text-xs">
              The radar chart shows your performance across multiple dimensions, compared to a baseline benchmark (dotted line).
              Areas inside the benchmark line indicate room for improvement.
              Scores are normalized to 0-100 based on industry averages and best practices.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DimensionCardProps {
  dimension: HealthDimension;
  isLoading: boolean;
}

function DimensionCard({ dimension, isLoading }: DimensionCardProps) {
  const StatusIcon = dimension.status === 'good' ? CheckCircle :
    dimension.status === 'warning' ? AlertCircle : AlertCircle;

  return (
    <div className={cn(
      'glass-panel p-4 border-l-4',
      dimension.status === 'good' && 'border-l-success bg-success/5',
      dimension.status === 'warning' && 'border-l-warning bg-warning/5',
      dimension.status === 'poor' && 'border-l-danger bg-danger/5'
    )}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-foreground flex items-center gap-2">
            {dimension.label}
            <StatusIcon className={cn(
              'h-4 w-4',
              dimension.status === 'good' && 'text-success',
              dimension.status === 'warning' && 'text-warning',
              dimension.status === 'poor' && 'text-danger'
            )} />
          </p>
          <p className="text-xs text-muted-foreground mt-1">{dimension.description}</p>
        </div>
        <div className={cn(
          'text-2xl font-bold',
          dimension.status === 'good' && 'text-success',
          dimension.status === 'warning' && 'text-warning',
          dimension.status === 'poor' && 'text-danger'
        )}>
          {isLoading ? '-' : dimension.score.toFixed(0)}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full transition-all duration-500',
            dimension.status === 'good' && 'bg-success',
            dimension.status === 'warning' && 'bg-warning',
            dimension.status === 'poor' && 'bg-danger'
          )}
          style={{ width: `${dimension.score}%` }}
        />
      </div>

      {/* Tip */}
      <p className="text-xs text-muted-foreground mt-2 flex items-start gap-1">
        <TrendingUp className="h-3 w-3 mt-0.5 flex-shrink-0" />
        {dimension.tip}
      </p>
    </div>
  );
}
